// src/slices/authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface AuthState {
  token?: string;
  email?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
}

const AUTH_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = "auth_state"; // single JSON key for auth object
const STORAGE_EXPIRES_KEY = "auth_expires";

const initialState: AuthState = {};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * payload: { token, email?, role?, firstName?, lastName? }
     * Stores token and metadata and persists expiry timestamp (24h).
     */
    setCredentials(
      state,
      action: PayloadAction<{
        token: string;
        email?: string;
        role?: string;
        firstName?: string;
        lastName?: string;
      }>
    ) {
      const { token, email, role, firstName, lastName } = action.payload;
      state.token = token;
      state.email = email;
      state.role = role;
      state.firstName = firstName;
      state.lastName = lastName;

      try {
        const payload = {
          token: token || null,
          email: email || null,
          role: role || null,
          firstName: firstName || null,
          lastName: lastName || null,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        const expiresAt = Date.now() + AUTH_TTL_MS;
        localStorage.setItem(STORAGE_EXPIRES_KEY, String(expiresAt));
        // dev helper
        // console.debug("auth: persisted", payload);
      } catch (err) {
        console.warn("auth: failed to persist credentials", err);
      }
    },

    /**
     * Clear credentials locally (logout)
     */
    clearCredentials(state) {
      state.token = undefined;
      state.email = undefined;
      state.role = undefined;
      state.firstName = undefined;
      state.lastName = undefined;
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRES_KEY);
      } catch (err) {
        // ignore
      }
    },

    /**
     * Try to load token + metadata from localStorage.
     * If token expired or missing, clear state.
     * This reducer runs synchronously.
     */
    loadFromStorage(state) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const expires = Number(
          localStorage.getItem(STORAGE_EXPIRES_KEY) || "0"
        );

        if (!raw || !expires) {
          state.token = undefined;
          state.email = undefined;
          state.role = undefined;
          state.firstName = undefined;
          state.lastName = undefined;
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_EXPIRES_KEY);
          return;
        }

        if (Date.now() > expires) {
          // expired
          state.token = undefined;
          state.email = undefined;
          state.role = undefined;
          state.firstName = undefined;
          state.lastName = undefined;
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_EXPIRES_KEY);
          return;
        }

        const parsed = JSON.parse(raw) as {
          token?: string | null;
          email?: string | null;
          role?: string | null;
          firstName?: string | null;
          lastName?: string | null;
        };

        state.token = parsed?.token || undefined;
        state.email = (parsed?.email as string) || undefined;
        state.role = (parsed?.role as string) || undefined;
        state.firstName = (parsed?.firstName as string) || undefined;
        state.lastName = (parsed?.lastName as string) || undefined;
      } catch (err) {
        // If localStorage access fails, ensure state empty
        state.token = undefined;
        state.email = undefined;
        state.role = undefined;
        state.firstName = undefined;
        state.lastName = undefined;
      }
    },
  },
});

export const { setCredentials, clearCredentials, loadFromStorage } =
  authSlice.actions;
export default authSlice.reducer;
